import { useEffect, useMemo } from 'react';

import { useAppSelector } from 'state/hooks';
import { useGetRouteTravelTimeBetweenDatesQuery } from 'state/savedRouteSlices';

import {
  DEFAULT_TIME_WITH_MERIDIEM,
  makeLocalDateFromStrings,
} from 'features/common/DateTimeSelector/utils';

import { MetricsRouteData } from '../../appTypes';
import {
  useGetRouteMetricsDayRangeQuery,
  useGetRouteMetricsQuery,
  useGetTypicalRouteMetricsQuery,
} from '../../state/apiSlice';
import {
  selectComparison,
  selectComparisonEndDate,
  selectIsMonthComparison,
  selectProject,
  selectRmEndDateTime,
  selectRmStartDateTime,
  selectRouteSelection,
  selectTargetDate,
  selectTargetEndDate,
  selectUseComparisonDayRange,
  selectUseDayRange,
} from '../../state/workflowSlice';
import {
  routeSelectionIsSavedRoute,
  useRouteInfo,
} from '../common/hooks/useRouteInfo';

export function useFromToDateTravelTimeData(skip: boolean): {
  nrt: MetricsRouteData;
  typical: MetricsRouteData;
} {
  const targetDate = useAppSelector(selectTargetDate);
  const project_slug = useAppSelector(selectProject);
  const comparison = useAppSelector(selectComparison);
  const routeSelection = useAppSelector(selectRouteSelection);
  const startDate: string = useAppSelector(selectRmStartDateTime);
  const endDate: string = useAppSelector(selectRmEndDateTime);
  const { basicRouteInfo } = useRouteInfo();
  const hasRequiredParams = Boolean(
    startDate &&
    endDate &&
    routeSelection &&
    comparison &&
    basicRouteInfo.realizedSegments &&
    targetDate,
  );
  const isTempRoute = !routeSelectionIsSavedRoute(routeSelection);

  const { start, end } = useMemo(() => {
    if (isTempRoute && targetDate) {
      // realtime temp
      return {
        start: makeLocalDateFromStrings(targetDate, DEFAULT_TIME_WITH_MERIDIEM),
        end: makeLocalDateFromStrings(
          targetDate,
          DEFAULT_TIME_WITH_MERIDIEM,
          true,
        ),
      };
    }
    return { start: startDate, end: endDate };
  }, [isTempRoute, targetDate, startDate, endDate]);

  const {
    currentData: data,
    refetch,
    isUninitialized,
  } = useGetRouteTravelTimeBetweenDatesQuery(
    {
      project_slug,
      start_date: start,
      end_date: end,
      savedRouteId: isTempRoute ? undefined : routeSelection,
      routeIds: isTempRoute ? basicRouteInfo.realizedSegments : undefined,
      typicalMonth: comparison,
    },
    {
      skip: skip || !hasRequiredParams,
    },
  ) as any;

  useEffect(() => {
    if (!skip && !isUninitialized) refetch();
  }, [skip]);

  return {
    nrt: data?.nrt ?? null,
    typical: data?.typical ?? null,
  };
}

export function useTargetDateMetrics(
  skip: boolean = false,
  pollingInterval: number = 0,
): {
  metrics: MetricsRouteData | undefined;
  comparisonData: MetricsRouteData | undefined;
} {
  const project_slug = useAppSelector(selectProject);
  const targetDate: string = useAppSelector(selectTargetDate);
  const comparison = useAppSelector(selectComparison);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const comparisonEndDate = useAppSelector(selectComparisonEndDate);
  const useDayRange = useAppSelector(selectUseDayRange);
  const isMonthComparison = useAppSelector(selectIsMonthComparison);
  const useComparisonDayRange = useAppSelector(selectUseComparisonDayRange);

  const { basicRouteInfo } = useRouteInfo();

  const routeSegments = basicRouteInfo?.realizedSegments ?? [];
  const hasSegments = routeSegments.length > 0;

  const singleDayMetrics = useGetRouteMetricsQuery(
    { project_slug, date: targetDate, routeIds: routeSegments },
    {
      skip: skip || useDayRange || !(hasSegments && targetDate && project_slug),
    },
  ) as any;

  const dayRangeMetrics = useGetRouteMetricsDayRangeQuery(
    {
      project_slug,
      routeIds: routeSegments,
      startDate: targetDate,
      endDate: targetEndDate ?? '',
    },
    {
      skip:
        skip ||
        !useDayRange ||
        !(hasSegments && targetDate && project_slug && targetEndDate),
    },
  ) as any;

  const typicalMetrics = useGetTypicalRouteMetricsQuery(
    {
      project_slug,
      typicalMonth: comparison,
      routeIds: routeSegments,
      date: targetDate,
    },
    {
      skip:
        skip ||
        !(comparison && hasSegments && targetDate && project_slug) ||
        !isMonthComparison,
    },
  ) as any;

  const comparisonDayRangeMetrics = useGetRouteMetricsDayRangeQuery(
    {
      project_slug,
      routeIds: routeSegments,
      startDate: comparison ?? '',
      endDate: comparisonEndDate ?? '',
    },
    {
      skip:
        skip ||
        !useComparisonDayRange ||
        !(comparison && hasSegments && project_slug && comparisonEndDate),
    },
  ) as any;

  const comparisonMetrics = useGetRouteMetricsQuery(
    { project_slug, date: comparison, routeIds: routeSegments },
    {
      skip:
        skip ||
        !(comparison && hasSegments) ||
        isMonthComparison ||
        useComparisonDayRange,
    },
  ) as any;

  // refetch all at the same time every {x} ms
  useEffect(() => {
    if (skip || !pollingInterval) return;

    const interval = setInterval(() => {
      // main metric
      const main = useDayRange ? dayRangeMetrics : singleDayMetrics;
      if (!main.isUninitialized) {
        main.refetch?.();
      }

      // comparison metric
      let comparison = comparisonMetrics;

      if (isMonthComparison) {
        comparison = typicalMetrics;
      } else if (useComparisonDayRange) {
        comparison = comparisonDayRangeMetrics;
      }

      if (!comparison.isUninitialized) {
        comparison.refetch?.();
      }
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [
    skip,
    pollingInterval,
    isMonthComparison,
    useDayRange,
    useComparisonDayRange,
    dayRangeMetrics,
    singleDayMetrics,
    typicalMetrics,
    comparisonMetrics,
    comparisonDayRangeMetrics,
  ]);

  const comparisonData =
    typicalMetrics.currentData ||
    (useComparisonDayRange
      ? comparisonDayRangeMetrics.currentData
      : comparisonMetrics.currentData);

  return {
    metrics: useDayRange
      ? dayRangeMetrics.currentData
      : singleDayMetrics.currentData,
    comparisonData,
  };
}
